import { useState, useRef, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";

interface EmployeeOption {
  id: string;
  name: string;
  email: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
  isAdmin: boolean;
  currentEmployeeId: string | null;
}

interface ParsedRow {
  order_number: string;
  customer: string;
  customer_type: string;
  product: string;
  quantity: number;
  unit: string;
  logistics: string;
  tracking_number: string;
  expected_arrival: string;
  delivery_deadline: string;
  amount: number;
  currency: string;
  status: string;
}

const HEADER_MAP: Record<string, string> = {
  "訂單編號": "order_number",
  "客戶": "customer",
  "客戶類型": "customer_type",
  "商品": "product",
  "數量": "quantity",
  "單位": "unit",
  "物流商": "logistics",
  "追蹤號碼": "tracking_number",
  "預計到港時間": "expected_arrival",
  "交付期限": "delivery_deadline",
  "金額": "amount",
  "幣別": "currency",
  "狀態": "status",
};

export default function ExcelImportModal({ open, onClose, onImported, isAdmin, currentEmployeeId }: Props) {
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [assigneeId, setAssigneeId] = useState(currentEmployeeId || "");
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [fileName, setFileName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successCount, setSuccessCount] = useState(0);
  const [step, setStep] = useState<"upload" | "preview" | "done">("upload");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    if (!open) return;
    supabase
      .from("employees")
      .select("id, name, email")
      .order("name")
      .then(({ data }) => {
        if (data) setEmployees(data as EmployeeOption[]);
      });
    setAssigneeId(currentEmployeeId || "");
    setParsedRows([]);
    setFileName("");
    setError("");
    setSuccessCount(0);
    setStep("upload");
  }, [open, currentEmployeeId]);

  const parseExcelDate = (val: unknown): string => {
    if (val instanceof Date) return val.toISOString().slice(0, 10);
    if (typeof val === "number") {
      // Excel serial date
      const d = new Date((val - 25569) * 86400 * 1000);
      return d.toISOString().slice(0, 10);
    }
    const s = String(val || "").trim();
    // Try common formats: YYYY-MM-DD, YYYY/MM/DD, MM/DD/YYYY
    const m = s.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/) || s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (m) {
      const [y, mo, d] = m[1].length === 4 ? [m[1], m[2], m[3]] : [m[3], m[1], m[2]];
      return `${y}-${mo.padStart(2, "0")}-${d.padStart(2, "0")}`;
    }
    return s;
  };

  const parseFile = useCallback(async (file: File) => {
    setError("");
    setFileName(file.name);

    try {
      const XLSX = await import("xlsx");
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data, { type: "array" });
      const sheetName = wb.SheetNames[0];
      const sheet = wb.Sheets[sheetName];
      const json = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: "" });

      if (json.length === 0) {
        setError("檔案中沒有資料");
        return;
      }

      const headers = Object.keys(json[0]);
      const columnMap: Record<string, string> = {};

      for (const h of headers) {
        const key = HEADER_MAP[h.trim()];
        if (key) columnMap[h] = key;
      }

      const missingRequired = ["訂單編號", "客戶", "商品"].filter((h) => !headers.some((k) => k.trim() === h));
      if (missingRequired.length > 0) {
        setError(`缺少必要欄位：${missingRequired.join("、")}`);
        return;
      }

      const rows: ParsedRow[] = json.map((row, idx) => {
        const get = (header: string) => {
          const found = Object.keys(row).find((k) => k.trim() === header);
          return found ? row[found] : "";
        };

        return {
          order_number: String(get("訂單編號") || "").trim(),
          customer: String(get("客戶") || "").trim(),
          customer_type: String(get("客戶類型") || "").trim(),
          product: String(get("商品") || "").trim(),
          quantity: parseInt(String(get("數量") || "1")) || 1,
          unit: String(get("單位") || "瓶").trim() || "瓶",
          logistics: String(get("物流商") || "").trim(),
          tracking_number: String(get("追蹤號碼") || "").trim(),
          expected_arrival: parseExcelDate(get("預計到港時間")),
          delivery_deadline: parseExcelDate(get("交付期限")),
          amount: parseFloat(String(get("金額") || "0")) || 0,
          currency: String(get("幣別") || "NT$").trim() || "NT$",
          status: String(get("狀態") || "海運中").trim() || "海運中",
        };
      });

      setParsedRows(rows);
      setStep("preview");
    } catch (err) {
      setError("檔案解析失敗，請確認格式正確");
    }
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    parseFile(file);
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) parseFile(file);
  };

  const handleImport = async () => {
    if (!assigneeId) {
      setError("請選擇指派員工");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      if (assigneeId === "__all__") {
        // Create one order per employee for each row
        const payloads = parsedRows.flatMap((row) =>
          employees.map((emp) => ({
            ...row,
            employee_id: emp.id,
          }))
        );
        const { error: insertErr } = await supabase.from("shipping_orders").insert(payloads);
        if (insertErr) throw new Error(insertErr.message);
        setSuccessCount(payloads.length);
      } else {
        const payloads = parsedRows.map((row) => ({
          ...row,
          employee_id: assigneeId,
        }));
        const { error: insertErr } = await supabase.from("shipping_orders").insert(payloads);
        if (insertErr) throw new Error(insertErr.message);
        setSuccessCount(payloads.length);
      }

      setStep("done");
      onImported();
    } catch (err) {
      setError(err instanceof Error ? err.message : "匯入失敗");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const inputClass = "w-full px-3 py-2 rounded-lg border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 bg-background-50";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-background-50 rounded-xl border border-background-200 shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto mx-4">
        {/* Header */}
        <div className="sticky top-0 bg-background-50 border-b border-background-200 px-6 py-4 flex items-center justify-between rounded-t-xl z-10">
          <div>
            <h3 className="text-lg font-bold text-foreground-900" style={{ fontFamily: "var(--font-heading)" }}>
              Excel 批量匯入
            </h3>
            <p className="text-xs text-foreground-500 mt-0.5">
              上傳 .xlsx 或 .csv 檔案，一次匯入多筆貨運訂單
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-background-100 flex items-center justify-center cursor-pointer transition-colors">
            <i className="ri-close-line text-foreground-500" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {error && (
            <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-accent-100 text-accent-700 text-sm">
              <i className="ri-error-warning-line" />
              {error}
            </div>
          )}

          {step === "upload" && (
            <>
              {/* Template download */}
              <div className="flex items-center gap-3 p-4 rounded-lg bg-background-100 border border-background-200">
                <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                  <i className="ri-download-2-line text-primary-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground-900">下載 Excel 模板</p>
                  <p className="text-xs text-foreground-500 mt-0.5">包含完整欄位格式，可直接填寫後上傳</p>
                </div>
                <button
                  onClick={() => {
                    const headers = ["訂單編號", "客戶", "客戶類型", "商品", "數量", "單位", "物流商", "追蹤號碼", "預計到港時間", "交付期限", "金額", "幣別", "狀態"];
                    const sample = ["SH-001", "範例公司", "經銷商", "紅酒", "100", "瓶", "Maersk", "MAEU1234567", "2026-08-01", "2026-08-15", "50000", "NT$", "海運中"];
                    const csv = "\uFEFF" + headers.join(",") + "\n" + sample.join(",");
                    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = "shipping_template.csv";
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  className="px-4 py-2 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap"
                >
                  下載模板
                </button>
              </div>

              {/* Upload zone */}
              <div
                ref={dropRef}
                className={`relative flex flex-col items-center justify-center p-10 rounded-xl border-2 border-dashed cursor-pointer transition-colors ${
                  dragOver ? "border-primary-400 bg-primary-50/50" : "border-background-300 hover:border-primary-300 hover:bg-background-50"
                }`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="w-14 h-14 rounded-full bg-primary-100 flex items-center justify-center mb-3">
                  <i className="ri-upload-cloud-2-line text-primary-500 text-xl" />
                </div>
                <p className="text-sm font-medium text-foreground-900">點擊上傳或拖曳檔案至此</p>
                <p className="text-xs text-foreground-400 mt-1">支援 .xlsx / .xls / .csv 格式</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </div>
            </>
          )}

          {step === "preview" && (
            <>
              {/* File info + re-upload */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-foreground-700">
                  <i className="ri-file-excel-2-line text-primary-500" />
                  {fileName}
                  <span className="text-xs text-foreground-400">（{parsedRows.length} 筆資料）</span>
                </div>
                <button
                  onClick={() => { setStep("upload"); setParsedRows([]); setFileName(""); }}
                  className="text-xs text-foreground-500 hover:text-foreground-700 cursor-pointer flex items-center gap-1"
                >
                  <i className="ri-arrow-left-line" />
                  重新上傳
                </button>
              </div>

              {/* Assignee selector */}
              <div className="flex items-center gap-3">
                <label className="text-xs font-medium text-foreground-600 whitespace-nowrap">指派員工 <span className="text-accent-500">*</span></label>
                <select value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)} className={inputClass + " max-w-xs"}>
                  <option value="">請選擇員工</option>
                  {isAdmin && <option value="__all__">── 全部員工（每人一筆）──</option>}
                  {isAdmin && <option disabled>──────────────</option>}
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name}（{emp.email}）
                    </option>
                  ))}
                </select>
                {assigneeId === "__all__" && (
                  <span className="text-xs text-primary-600 flex items-center gap-1 whitespace-nowrap">
                    <i className="ri-information-line" />
                    {parsedRows.length} 行 × {employees.length} 人 = {parsedRows.length * employees.length} 筆
                  </span>
                )}
              </div>

              {/* Preview table */}
              <div className="overflow-x-auto border border-background-200 rounded-lg">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-background-200 bg-background-100">
                      <th className="text-left px-3 py-2 font-medium text-foreground-600 whitespace-nowrap">訂單編號</th>
                      <th className="text-left px-3 py-2 font-medium text-foreground-600 whitespace-nowrap">客戶</th>
                      <th className="text-left px-3 py-2 font-medium text-foreground-600 whitespace-nowrap">商品</th>
                      <th className="text-right px-3 py-2 font-medium text-foreground-600 whitespace-nowrap">數量</th>
                      <th className="text-left px-3 py-2 font-medium text-foreground-600 whitespace-nowrap">物流商</th>
                      <th className="text-left px-3 py-2 font-medium text-foreground-600 whitespace-nowrap">預計到港</th>
                      <th className="text-right px-3 py-2 font-medium text-foreground-600 whitespace-nowrap">金額</th>
                      <th className="text-left px-3 py-2 font-medium text-foreground-600 whitespace-nowrap">狀態</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedRows.slice(0, 20).map((row, idx) => (
                      <tr key={idx} className="border-b border-background-100 hover:bg-background-50/60">
                        <td className="px-3 py-2 font-mono text-foreground-700 whitespace-nowrap">{row.order_number}</td>
                        <td className="px-3 py-2 text-foreground-900 whitespace-nowrap">{row.customer}</td>
                        <td className="px-3 py-2 text-foreground-700 whitespace-nowrap">{row.product}</td>
                        <td className="px-3 py-2 text-right text-foreground-700 whitespace-nowrap">{row.quantity} {row.unit}</td>
                        <td className="px-3 py-2 text-foreground-600 whitespace-nowrap">{row.logistics}</td>
                        <td className="px-3 py-2 text-foreground-600 whitespace-nowrap">{row.expected_arrival}</td>
                        <td className="px-3 py-2 text-right text-foreground-900 font-medium whitespace-nowrap">NT$ {row.amount.toLocaleString("zh-TW")}</td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <span className="px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-secondary-100 text-secondary-700">{row.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {parsedRows.length > 20 && (
                  <div className="px-3 py-2 text-xs text-foreground-400 bg-background-50 border-t border-background-100">
                    ...還有 {parsedRows.length - 20} 筆資料
                  </div>
                )}
              </div>
            </>
          )}

          {step === "done" && (
            <div className="flex flex-col items-center py-8">
              <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center mb-4">
                <i className="ri-check-line text-primary-500 text-2xl" />
              </div>
              <p className="text-lg font-bold text-foreground-900">匯入完成！</p>
              <p className="text-sm text-foreground-500 mt-1">成功匯入 {successCount} 筆訂單</p>
              <button
                onClick={() => { setStep("upload"); setParsedRows([]); setFileName(""); setSuccessCount(0); }}
                className="mt-4 px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap"
              >
                繼續匯入
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        {step === "preview" && (
          <div className="sticky bottom-0 bg-background-50 border-t border-background-200 px-6 py-4 flex items-center justify-end gap-3 rounded-b-xl">
            <button
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2.5 rounded-lg border border-background-300 text-foreground-700 text-sm font-medium hover:bg-background-100 cursor-pointer transition-colors whitespace-nowrap"
            >
              取消
            </button>
            <button
              onClick={handleImport}
              disabled={submitting}
              className="px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap flex items-center gap-2 disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  匯入中...
                </>
              ) : (
                <>
                  <i className="ri-upload-line" />
                  確認匯入
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}